import { useGetPopupContainer } from '@/components/ReportForm/Container/PopupContainer';
import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import { useTranslation } from '@/i18n';
import {
  FormSelect,
  FormSelectProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { usePrefectureOptions } from './hooks/usePrefectureOptions';
import { usePrefectureSearch } from './hooks/usePrefectureSearch';

export type Props<TFieldValues extends FieldValues> = Omit<
  InputBaseType<TFieldValues>,
  'label' | 'tooltip' | 'caption' | 'hidden'
> &
  Omit<FormSelectProps<TFieldValues>, 'selectProps'>;

const InnerContactAddressPrefecture = <TFieldValues extends FieldValues>({
  control,
  name,
  inputId,
  required = false,
}: Props<TFieldValues>) => {
  const { t } = useTranslation();
  const containerRef = useGetPopupContainer();

  // Get prefecture options from hook
  const { prefectureOptions, isLoading } = usePrefectureOptions();
  const { filteredOptions, handleSearch } =
    usePrefectureSearch(prefectureOptions);

  const rules = useMemo(
    () => ({
      required: required ? t('required') : false,
    }),
    [required, t]
  );

  const labelProps = useMemo(
    () => ({
      text: t('ap_payee_prefecture'),
    }),
    [t]
  );

  const selectProps = useMemo(
    () => ({
      options: filteredOptions,
      placeholder: '',
      dropdownStyle: { width: 240 },
      getPopupContainer: containerRef,
      comboBox: true,
      inputPlaceholder: t('ap_payee_prefecture_search_placeholder'),
      onSearch: handleSearch,
      filterOption: false,
      loading: isLoading,
    }),
    [containerRef, t, filteredOptions, handleSearch, isLoading]
  );

  return (
    <FormSelect<TFieldValues>
      label={labelProps}
      control={control}
      name={name}
      inputId={inputId}
      rules={rules}
      selectProps={selectProps}
    />
  );
};

InnerContactAddressPrefecture.displayName = 'InnerContactAddressPrefecture';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ContactAddressPrefecture = memo((props) => (
  <InnerContactAddressPrefecture {...props} />
)) as typeof InnerContactAddressPrefecture;
ContactAddressPrefecture.displayName = 'ContactAddressPrefecture';
